import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Avatar,
  Badge,
  Button,
  Flex,
  Icon,
  Select,
  Td,
  Text,
  Tooltip,
  Tr,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import axios from "axios";
import config from "config";
import React, { useRef, useState } from "react";
import { FaTrashAlt, FaShieldAlt, FaExclamationTriangle } from "react-icons/fa";
import { getToken, getRole, getEmail, isAdmin, isModerator } from "utils/auth";

function TablesUserRow(props) {
  const { logo, first_name, last_name, email, role, func, setUserProfiles, currentUserEmail } = props;
  const textColor = useColorModeValue("gray.700", "white");
  const bgStatus = useColorModeValue("gray.400", "#1a202c");
  const colorStatus = useColorModeValue("white", "gray.400");
  const dialogBg = useColorModeValue("white", "navy.800");
  const previewBg = useColorModeValue("gray.50", "navy.900");
  const previewBorder = useColorModeValue("gray.200", "whiteAlpha.200");
  const toast = useToast();

  const [updatingRole, setUpdatingRole] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pendingRole, setPendingRole] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const cancelRoleRef = useRef();
  const cancelDeleteRef = useRef();

  const currentRole = getRole();
  const myEmail = currentUserEmail || getEmail();
  const currentIsAdmin = isAdmin();
  const currentIsModerator = isModerator();
  const isTargetAdmin = role === "admin";
  const isSelf = Boolean(myEmail && email && myEmail.toLowerCase() === email.toLowerCase());

  // Permission rules:
  // - Only administrators can change member roles or delete members.
  // - Admin can delete/change role of another admin, but not himself.
  // - Moderators CANNOT change roles or delete members.
  const canChangeRole = !isSelf && currentIsAdmin;
  const canDeleteTarget = !isSelf && currentIsAdmin;

  const promptRoleChange = (newRole) => {
    if (newRole === role) return;
    if (isSelf) {
      toast({
        title: "Action Restricted",
        description: "You cannot change your own role.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (!canChangeRole) {
      toast({
        title: "Action Restricted",
        description: currentIsModerator
          ? "Moderators cannot change member roles (Administrators only)."
          : "Only administrators can change member roles.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    setPendingRole(newRole);
    setIsConfirmOpen(true);
  };

  const handleCancelRoleChange = () => {
    setPendingRole(null);
    setIsConfirmOpen(false);
  };

  const handleConfirmRoleChange = async () => {
    if (!pendingRole) return;
    const targetRole = pendingRole;

    try {
      setUpdatingRole(true);
      const token = getToken();
      await axios.put(
        `${config.API_URL}/user/${email}/role`,
        { role: targetRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const res = await axios.get(`${config.API_URL}/user/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUserProfiles(res.data);

      toast({
        title: "Role updated successfully",
        description: `${first_name || email} is now ${targetRole.toUpperCase()}.`,
        status: "success",
        duration: 3500,
        isClosable: true,
      });
      setIsConfirmOpen(false);
      setPendingRole(null);
    } catch (error) {
      toast({
        title: "Update Failed",
        description: error.response?.data?.error || error.response?.data?.message || "Failed to update role.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdatingRole(false);
    }
  };

  const promptDelete = () => {
    if (isSelf) {
      toast({
        title: "Action Forbidden",
        description: "You cannot delete your own account.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (!canDeleteTarget) {
      toast({
        title: "Action Forbidden",
        description: currentIsModerator
          ? "Moderators cannot delete members (Administrators only)."
          : "Only administrators can delete members.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    setIsDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);
      const token = getToken();
      await axios.delete(`${config.API_URL}/user/` + email, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const res = await axios.get(`${config.API_URL}/user/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUserProfiles(res.data);

      toast({
        title: "User deleted",
        description: `Account ${email} has been removed.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setIsDeleteOpen(false);
    } catch (error) {
      toast({
        title: "Delete Failed",
        description: error.response?.data?.error || error.response?.data?.message || "Cannot delete user.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      console.error("Error Delete:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Tr>
        <Td minWidth={{ sm: "250px" }} pl="0px">
          <Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
            <Avatar src={logo} w="50px" borderRadius="12px" me="18px" />
            <Flex direction="column">
              <Text
                fontSize="md"
                color={textColor}
                fontWeight="bold"
                minWidth="100%"
              >
                {first_name} {last_name}
              </Text>
              <Text fontSize="sm" color="gray.400" fontWeight="normal">
                {email}
              </Text>
            </Flex>
          </Flex>
        </Td>

        {/* Role Management Column */}
        <Td>
          {isSelf ? (
            <Tooltip label="You cannot change your own role" hasArrow>
              <Badge
                colorScheme={role === "admin" ? "purple" : role === "moderator" ? "blue" : "teal"}
                variant="solid"
                borderRadius="full"
                px="12px"
                py="4px"
                fontSize="xs"
                fontWeight="800"
                display="inline-flex"
                alignItems="center"
                gap="4px"
              >
                {role === "admin" && <Icon as={FaShieldAlt} />}
                {role.toUpperCase()} (YOU)
              </Badge>
            </Tooltip>
          ) : canChangeRole ? (
            <Select
              size="sm"
              w="130px"
              borderRadius="10px"
              value={role}
              isDisabled={updatingRole}
              onChange={(e) => promptRoleChange(e.target.value)}
              fontWeight="600"
              colorScheme="teal"
              cursor="pointer"
            >
              {currentIsAdmin && <option value="admin">Admin</option>}
              <option value="moderator">Moderator</option>
              <option value="user">User</option>
            </Select>
          ) : (
            <Tooltip
              label={
                currentIsModerator
                  ? "Moderators cannot change member roles (Admin only)"
                  : "Only administrators can change member roles"
              }
              hasArrow
            >
              <Badge
                colorScheme={role === "admin" ? "purple" : role === "moderator" ? "blue" : "teal"}
                variant="solid"
                borderRadius="full"
                px="12px"
                py="4px"
                fontSize="xs"
                fontWeight="800"
                display="inline-flex"
                alignItems="center"
                gap="4px"
              >
                {role === "admin" && <Icon as={FaShieldAlt} />}
                {role.toUpperCase()}
              </Badge>
            </Tooltip>
          )}
        </Td>

        {/* Function / Title */}
        <Td>
          <Badge
            bg={bgStatus}
            color={colorStatus}
            fontSize="sm"
            p="4px 12px"
            borderRadius="8px"
          >
            {func || "Researcher"}
          </Badge>
        </Td>

        {/* Action / Delete Button */}
        <Td>
          {canDeleteTarget ? (
            <Button
              p="0px"
              bg="transparent"
              mb={{ sm: "10px", md: "0px" }}
              me={{ md: "12px" }}
              onClick={promptDelete}
            >
              <Flex color="red.500" cursor="pointer" align="center" p="12px" _hover={{ color: "red.700" }}>
                <Icon as={FaTrashAlt} me="4px" />
                <Text fontSize="sm" fontWeight="semibold">
                  DELETE
                </Text>
              </Flex>
            </Button>
          ) : (
            <Tooltip
              label={
                isSelf
                  ? "You cannot delete your own account"
                  : currentIsModerator
                  ? "Moderators cannot delete members (Admin only)"
                  : "Only administrators can delete members"
              }
              hasArrow
            >
              <Badge colorScheme="gray" variant="outline" borderRadius="8px" px="8px" py="4px">
                {isSelf ? "Current User" : "Protected"}
              </Badge>
            </Tooltip>
          )}
        </Td>
      </Tr>

      {/* Role Change Confirmation Dialog */}
      <AlertDialog
        isOpen={isConfirmOpen}
        leastDestructiveRef={cancelRoleRef}
        onClose={handleCancelRoleChange}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg={dialogBg} borderRadius="16px" mx="16px">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              <Flex align="center" gap="10px">
                <Flex
                  w="36px"
                  h="36px"
                  borderRadius="10px"
                  bg="teal.50"
                  color="teal.500"
                  align="center"
                  justify="center"
                >
                  <Icon as={FaShieldAlt} w="18px" h="18px" />
                </Flex>
                <Text>Confirm Role Change</Text>
              </Flex>
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text fontSize="sm" color={textColor} mb="14px">
                Are you sure you want to update permissions for{" "}
                <strong>{first_name} {last_name}</strong> (<em>{email}</em>)?
              </Text>

              <Flex
                align="center"
                justify="center"
                bg={previewBg}
                p="14px"
                borderRadius="12px"
                gap="14px"
                border="1px solid"
                borderColor={previewBorder}
              >
                <Badge
                  colorScheme={role === "admin" ? "purple" : role === "moderator" ? "blue" : "teal"}
                  px="12px"
                  py="5px"
                  borderRadius="8px"
                  fontSize="xs"
                  fontWeight="bold"
                >
                  {role?.toUpperCase()}
                </Badge>
                <Text fontWeight="bold" color="gray.400">→</Text>
                <Badge
                  colorScheme={pendingRole === "admin" ? "purple" : pendingRole === "moderator" ? "blue" : "teal"}
                  px="12px"
                  py="5px"
                  borderRadius="8px"
                  fontSize="xs"
                  fontWeight="bold"
                >
                  {pendingRole?.toUpperCase()}
                </Badge>
              </Flex>

              {pendingRole === "admin" && (
                <Text fontSize="xs" color="purple.500" mt="12px" fontWeight="medium">
                  Note: Administrators have full authority to modify clinical data, invite new users, and manage platform roles.
                </Text>
              )}
            </AlertDialogBody>

            <AlertDialogFooter gap="10px">
              <Button
                ref={cancelRoleRef}
                onClick={handleCancelRoleChange}
                variant="ghost"
                borderRadius="10px"
              >
                Cancel
              </Button>
              <Button
                colorScheme="teal"
                onClick={handleConfirmRoleChange}
                isLoading={updatingRole}
                loadingText="Updating..."
                borderRadius="10px"
              >
                Confirm Change
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Delete User Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelDeleteRef}
        onClose={() => setIsDeleteOpen(false)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg={dialogBg} borderRadius="16px" mx="16px">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              <Flex align="center" gap="10px">
                <Flex
                  w="36px"
                  h="36px"
                  borderRadius="10px"
                  bg="red.50"
                  color="red.500"
                  align="center"
                  justify="center"
                >
                  <Icon as={FaExclamationTriangle} w="18px" h="18px" />
                </Flex>
                <Text>Delete Account</Text>
              </Flex>
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text fontSize="sm" color={textColor}>
                Are you sure you want to permanently delete the account of{" "}
                <strong>{first_name} {last_name}</strong> (<em>{email}</em>)?
              </Text>
              <Text fontSize="xs" color="red.400" mt="8px">
                This action cannot be undone. Associated posts and profile data will be permanently removed.
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter gap="10px">
              <Button
                ref={cancelDeleteRef}
                onClick={() => setIsDeleteOpen(false)}
                variant="ghost"
                borderRadius="10px"
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleConfirmDelete}
                isLoading={isDeleting}
                loadingText="Deleting..."
                borderRadius="10px"
              >
                Delete Account
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

export default TablesUserRow;
